import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowRight, Mail, Phone } from 'lucide-react';
import { NAV_LINKS, BRAND, SIGNUP_URL } from '../data/mock';
import { Button } from './ui/button';

const LOGO_TEXT = 'https://customer-assets-v7afamib.emergentagent.net/job_whatsapp-api-hub-13/artifacts/s9m1jzqb_ChatGPT%20Image%20Jul%209%2C%202026%2C%2001_28_29%20PM.png';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      {/* Utility bar */}
      <div className="hidden md:block bg-brand-purple text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-9 flex items-center justify-between text-[12px]">
          <div className="flex items-center gap-2 text-white/90">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-green animate-pulse" />
            <span className="font-medium">Live: Now serving 500+ businesses across India</span>
          </div>
          <div className="flex items-center gap-5 text-white/85">
            <a href={`mailto:${BRAND.email}`} className="flex items-center gap-1.5 hover:text-brand-green transition-colors">
              <Mail size={12} /> {BRAND.email}
            </a>
            <a href={`tel:+91${BRAND.phone}`} className="flex items-center gap-1.5 hover:text-brand-green transition-colors">
              <Phone size={12} /> +91 {BRAND.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main navbar - floating pill card */}
      <div className={`transition-all duration-300 ${scrolled ? 'py-2' : 'py-3 md:py-4'}`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className={`bg-white border border-slate-200 rounded-2xl shadow-lg shadow-slate-200/40 flex items-center justify-between px-4 lg:px-6 transition-all duration-300 ${scrolled ? 'h-14' : 'h-16 md:h-[68px]'}`}>
            <Link to="/" className="flex items-center shrink-0">
              <img src={LOGO_TEXT} alt="ACTIWA" className="h-8 md:h-9 w-auto" />
            </Link>

            <nav className="hidden lg:flex items-center gap-1 bg-slate-50 rounded-full p-1 border border-slate-100">
              {NAV_LINKS.map((l, i) => {
                const active = location.pathname === l.to;
                return (
                  <div
                    key={l.label}
                    className="relative"
                    onMouseEnter={() => setHoverIdx(i)}
                    onMouseLeave={() => setHoverIdx(null)}
                  >
                    <NavLink
                      to={l.to}
                      className={`px-3.5 py-1.5 text-[13px] font-semibold rounded-full flex items-center gap-1 transition-all ${
                        active
                          ? 'bg-white text-brand-purple shadow-sm'
                          : 'text-slate-600 hover:text-brand-purple'
                      }`}
                    >
                      {l.label}
                      {l.children && <ChevronDown size={12} className="opacity-70" />}
                    </NavLink>
                    {l.children && hoverIdx === i && (
                      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-60">
                        <div className="bg-white border border-slate-200 rounded-xl shadow-xl p-2">
                          {l.children.map((c) => (
                            <Link
                              key={c.label}
                              to={c.to}
                              className="block px-3 py-2 text-sm text-slate-700 rounded-md hover:bg-brand-purple-light hover:text-brand-purple"
                            >
                              {c.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <a href={SIGNUP_URL} target="_blank" rel="noreferrer" className="text-sm font-semibold text-slate-700 hover:text-brand-purple">
                Log in
              </a>
              <a href={SIGNUP_URL} target="_blank" rel="noreferrer">
                <Button className="bg-brand-purple hover:bg-brand-purple-dark text-white rounded-full px-5 group">
                  Start Free Trial
                  <ArrowRight size={15} className="ml-1.5 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </a>
            </div>

            <button
              className="lg:hidden p-2 rounded-md text-slate-800"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 mx-4 rounded-2xl mt-2 shadow-lg">
          <div className="px-5 py-4 space-y-1">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                onClick={() => setOpen(false)}
                className="block px-3 py-2.5 rounded-md text-slate-800 hover:bg-brand-purple-light"
              >
                {l.label}
              </Link>
            ))}
            <div className="pt-3 flex gap-2">
              <a href={SIGNUP_URL} target="_blank" rel="noreferrer" className="flex-1" onClick={() => setOpen(false)}>
                <Button variant="outline" className="w-full rounded-full">Log in</Button>
              </a>
              <a href={SIGNUP_URL} target="_blank" rel="noreferrer" className="flex-1" onClick={() => setOpen(false)}>
                <Button className="w-full bg-brand-purple hover:bg-brand-purple-dark text-white rounded-full">
                  Free Trial
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
